import { getLiffId, liffLoginUrl } from '../lib/liff';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';
import { Sheet } from './ui';
import { IcLine } from './icons';

const DEV_HANDLES = ['mojo', 'pun', 'aom', 'man'];

/** Dev login is a server setting (DEV_LOGIN="1" in local + demo envs); ask once, cache for the page lifetime. */
let devEnabled: boolean | null = null;
function useDevLogin(): boolean {
  const [on, setOn] = useState(devEnabled === true);
  useEffect(() => {
    if (devEnabled !== null) return;
    fetch('/api/auth/dev', { credentials: 'same-origin' })
      .then((r) => { devEnabled = r.ok; setOn(r.ok); })
      .catch(() => { devEnabled = false; });
  }, []);
  return on;
}

/**
 * The LIFF button only exists where a LIFF app points at this origin. Staging has no LIFF id, so the
 * button is hidden there rather than rendered pointing at the live site.
 */
function useLiffId(): string | null {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => { let alive = true; getLiffId().then((v) => { if (alive) setId(v); }); return () => { alive = false; }; }, []);
  return id;
}

/** `?login_error=<code>` set by /api/auth/line/callback when LINE Login fails — see LoginErrorCode on the server. */
const LOGIN_ERROR_TH: Record<string, string> = {
  cancelled: 'ยกเลิกการเข้าสู่ระบบด้วย LINE — ลองใหม่ได้ทุกเมื่อ',
  missing_params: 'LINE ส่งข้อมูลกลับมาไม่ครบ กรุณาลองเข้าสู่ระบบใหม่',
  state_expired: 'ลิงก์เข้าสู่ระบบหมดอายุ (เกิน 10 นาที) กรุณาลองใหม่',
  token_exchange: 'LINE ไม่ยอมรับรหัสเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง',
  no_id_token: 'LINE ไม่ส่งข้อมูลตัวตนกลับมา กรุณาลองใหม่',
  verify_failed: 'ตรวจสอบตัวตนกับ LINE ไม่สำเร็จ กรุณาลองใหม่',
  banned: 'บัญชีนี้ถูกระงับการใช้งาน ติดต่อทีมงานหากคิดว่าเป็นความผิดพลาด',
  server: 'ระบบขัดข้องระหว่างเข้าสู่ระบบ กรุณาลองใหม่อีกสักครู่',
};

/** Shows the Thai message for `?login_error`, strips the param from the URL, and re-opens the login sheet (except when banned). */
function useLoginErrorNotice() {
  const { query, url, route } = useLocation();
  const { openLogin } = useAuth();
  const toast = useToast();
  const code = query.login_error;
  useEffect(() => {
    if (!code) return;
    toast.error(LOGIN_ERROR_TH[code] ?? LOGIN_ERROR_TH.server!);
    const u = new URL(url, location.origin);
    u.searchParams.delete('login_error');
    const clean = u.pathname + u.search;
    route(clean, true);
    if (code !== 'banned') openLogin(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
}

/** Global bottom sheet. Auth links use target="_top" so the SPA router lets the browser navigate. */
export function LoginSheet() {
  const { loginReturnTo, closeLogin, loginError } = useAuth();
  const rt = encodeURIComponent(loginReturnTo || '/');
  const dev = useDevLogin();
  const liffId = useLiffId();
  useLoginErrorNotice();
  return (
    <Sheet open={loginReturnTo !== null} onClose={closeLogin} title="เข้าสู่ระบบ">
      <p class="muted small">เข้าร่วมตี้ เปิดตี้ และลงรูปได้เมื่อเข้าสู่ระบบ ใช้บัญชี LINE ของคุณ ไม่ต้องสมัครใหม่</p>
      {loginError && <p class="notice mt-16" role="alert">{loginError}</p>}
      {liffId && (
        <>
          <a class="btn line lg block mt-16" href={liffLoginUrl(liffId, loginReturnTo || '/')} target="_top"><IcLine size={22} /> เปิดใน LINE เพื่อเข้าสู่ระบบ</a>
          <p class="tiny muted mt-8">บนมือถือ ใช้บัญชีที่ล็อกอินอยู่ในแอป LINE ได้ ครั้งแรกอาจต้องกดยินยอม</p>
        </>
      )}
      <a class={`btn block mt-16 ${liffId ? 'secondary' : 'line lg'}`} href={`/api/auth/line?return_to=${rt}`} target="_top">
        {liffId ? 'เข้าสู่ระบบผ่านเบราว์เซอร์ / QR Code' : <><IcLine size={22} /> เข้าสู่ระบบด้วย LINE</>}
      </a>
      <p class="tiny muted mt-16" style={{ textAlign: 'center' }}>
        เราได้รับจาก LINE เพียงรหัสผู้ใช้ ชื่อ และรูปโปรไฟล์ — ไม่เห็นรหัสผ่าน เบอร์ เพื่อน หรือแชท
        <br />
        หลังเข้าสู่ระบบจะมีขั้นตอนยอมรับ <a href="/terms" onClick={closeLogin}>ข้อกำหนด</a> และ <a href="/privacy" onClick={closeLogin}>นโยบายความเป็นส่วนตัว</a>
      </p>
      {dev && (
        <div class="dev-row">
          <span>Dev: เข้าเป็น</span>
          {DEV_HANDLES.map((h) => (
            <a key={h} href={`/api/auth/dev?as=${h}&return_to=${rt}`} target="_top">
              {h}
            </a>
          ))}
        </div>
      )}
    </Sheet>
  );
}
